// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';

// interfaces/types
import type { StateTree, VAutoItem } from '@makemydeal/dr-dash-types';

// action types
import {
    VAUTO_APPRAISAL_LAUNCHED,
    VAUTO_APPRAISAL_MESSAGE_RECEIVED,
    VAUTO_APPRAISAL_MESSAGE_REJECTED,
    VAUTO_APPRAISAL_SLIDEOUT_LOADED,
    VAUTO_APPRAISALS_FAILURE,
    VAUTO_APPRAISALS_SUCCESS
} from '../actionTypes/vAutoActionTypes';

// selectors
import { getOfferCreationDateString } from '../selectors/offerRedux';
import * as tradeInsSelectors from '../selectors/tradeIns';

// utils
import { createStoreScopeState } from '../utils/storeScopeState';
import { buildUsabilityContext } from '../utils/usabilityContext';
import { mapTradeInsToAppraisals, shouldOverrideDealAcvWithVAutoAppraisal } from '../utils/vAutoUtils';
import {
    trackVAutoAcvFetchCompleted,
    trackVAutoAcvSyncOutcome,
    trackVAutoAppraisalLaunched,
    trackVAutoAppraisalMessageReceived,
    trackVAutoAppraisalMessageRejected,
    trackVAutoAppraisalSlideOutLoaded
} from '../utils/vAutoAppraisalTelemetry';
import type { VAutoAcvFetchTrigger } from '../utils/vAutoAppraisalTelemetry';

type AppraisalTimings = {
    launchedAt?: number;
    loadedAt?: number;
};

const getAppraisalTimings = createStoreScopeState<AppraisalTimings>(() => ({}));

const elapsedSince = (startedAt: number | undefined): number | undefined =>
    typeof startedAt === 'number' ? performance.now() - startedAt : undefined;

// All telemetry work goes in here: these dispatches are followed immediately by `onClose()` /
// `openSlideOut(...)`, so an escaping throw would skip them. Covers state reads and context building too,
// which emit()'s own guard does not.
const safely = (emitTelemetry: () => void): void => {
    try {
        emitTelemetry();
    } catch {
        // Best-effort by design.
    }
};

const resolveTrigger = (action: AnyFSA): VAutoAcvFetchTrigger => (action.meta?.isRefresh ? 'slideOutClose' : 'dealLoad');

type TradeAcvSnapshot = {
    index: number;
    acvBefore: number;
    appraisal: VAutoItem;
};

// Must mirror vAutoMiddleware's `vin && appraisal && appraisal.value > 0` guard, or we report writes that
// were never attempted.
const snapshotTradeAcvs = (state: StateTree, appraisals: VAutoItem[]): TradeAcvSnapshot[] =>
    mapTradeInsToAppraisals(state, appraisals)
        .filter(({ vin, appraisal }) => Boolean(vin) && Boolean(appraisal) && (appraisal as VAutoItem).value > 0)
        .map(({ index, appraisal }) => ({
            index,
            acvBefore: tradeInsSelectors.getTradeACV(index)(state),
            appraisal: appraisal as VAutoItem
        }));

// One event per appraised trade-in. `overrideApplied` calls the same shared util vAutoMiddleware uses, so
// it cannot drift; `acvChanged` is observed either side of `next`, which requires this middleware to be
// registered BEFORE vAutoMiddleware (it writes the ACV before its own `next`).
// remainingMiddleware.spec.ts asserts that position.
const trackAcvSyncOutcomes = (store: DashStore, action: AnyFSA, snapshots: TradeAcvSnapshot[], preState: StateTree): void => {
    if (!snapshots.length) {
        return;
    }

    const state = store.getState();
    const context = buildUsabilityContext(state);
    const trigger = resolveTrigger(action);
    const acvOverridePermitted = dealerSelectors.isTradeAcvOverrideEnabled(preState);
    const offerCreatedOn = getOfferCreationDateString(preState);

    snapshots.forEach(({ index, acvBefore, appraisal }) => {
        const acvAfter = tradeInsSelectors.getTradeACV(index)(state);

        trackVAutoAcvSyncOutcome(
            {
                trigger,
                tradeInIndex: index,
                overrideApplied: shouldOverrideDealAcvWithVAutoAppraisal(
                    acvOverridePermitted,
                    offerCreatedOn,
                    appraisal.updatedOn,
                    appraisal.value,
                    acvBefore
                ),
                acvChanged: acvAfter !== acvBefore,
                acvBefore,
                acvAfter,
                appraisalValue: appraisal.value,
                appraisalStatus: appraisal.status || '',
                acvOverridePermitted,
                hasOfferCreatedOn: Boolean(offerCreatedOn),
                hasAppraisalUpdatedOn: Boolean(appraisal.updatedOn)
            },
            context
        );
    });
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    switch (action?.type) {
        // Every branch: telemetry after next(action), inside safely(). Asserted by spec.
        case VAUTO_APPRAISAL_LAUNCHED: {
            const result = next(action);
            safely(() => {
                const timings = getAppraisalTimings(store);
                timings.launchedAt = performance.now();
                timings.loadedAt = undefined;

                trackVAutoAppraisalLaunched(action.payload, buildUsabilityContext(store.getState()));
            });
            return result;
        }
        case VAUTO_APPRAISAL_SLIDEOUT_LOADED: {
            const result = next(action);
            safely(() => {
                const timings = getAppraisalTimings(store);
                const durationMs = elapsedSince(timings.launchedAt);
                // Consumed once: a mid-panel iframe reload dispatches a second LOADED, and re-measuring it
                // from the original click would fold the manager's whole dwell into click-to-loaded.
                timings.launchedAt = undefined;
                timings.loadedAt = performance.now();

                trackVAutoAppraisalSlideOutLoaded({ ...action.payload, durationMs }, buildUsabilityContext(store.getState()));
            });
            return result;
        }
        case VAUTO_APPRAISAL_MESSAGE_RECEIVED: {
            const result = next(action);
            safely(() => {
                const { loadedAt } = getAppraisalTimings(store);
                trackVAutoAppraisalMessageReceived(
                    { ...action.payload, dwellMs: elapsedSince(loadedAt) },
                    buildUsabilityContext(store.getState())
                );
            });
            return result;
        }
        case VAUTO_APPRAISAL_MESSAGE_REJECTED: {
            const result = next(action);
            safely(() => trackVAutoAppraisalMessageRejected(action.payload, buildUsabilityContext(store.getState())));
            return result;
        }
        case VAUTO_APPRAISALS_SUCCESS: {
            const appraisals: VAutoItem[] = action.payload?.data?.items ?? [];
            let preState: StateTree | undefined;
            let snapshots: TradeAcvSnapshot[] = [];
            // Must precede next(action): vAutoMiddleware writes the ACV downstream of it.
            safely(() => {
                preState = store.getState();
                snapshots = snapshotTradeAcvs(preState, appraisals);
            });

            const result = next(action);

            safely(() => {
                trackVAutoAcvFetchCompleted(
                    {
                        outcome: 'success',
                        trigger: resolveTrigger(action),
                        appraisalCount: appraisals.length,
                        durationMs: elapsedSince(action.meta?.fetchStartTime)
                    },
                    buildUsabilityContext(store.getState())
                );
                if (preState) {
                    trackAcvSyncOutcomes(store, action, snapshots, preState);
                }
            });

            return result;
        }
        case VAUTO_APPRAISALS_FAILURE: {
            const result = next(action);

            safely(() =>
                trackVAutoAcvFetchCompleted(
                    {
                        outcome: 'failure',
                        trigger: resolveTrigger(action),
                        appraisalCount: 0,
                        durationMs: elapsedSince(action.meta?.fetchStartTime),
                        errorStatus: action.payload?.status,
                        errorMessage: action.payload?.message
                    },
                    buildUsabilityContext(store.getState())
                )
            );

            return result;
        }
        default: {
            return next(action);
        }
    }
};
